import { EyeIcon } from "@heroicons/react/24/outline";
import { useEffect, useRef, useState } from "react";
import { MessageRole, MessageRow } from "../../api/message.type";
import { useAuth } from "../../hooks/AuthContext";
import { useMessageContext } from "../../hooks/MessageContext";
import { useEventStreaming } from "../../hooks/useMessageStream";
import AssistantChatItem from "../chat/assistant-chat-item";
import ToolCallItem from "../chat/tool-call-item";
import UserChatItem from "../chat/user-chat-item";
import { RunStepsModal } from "../run-steps/run-steps-modal";
import { SubmitButton } from "../shared/buttons/submit-button";
import ThemedSpinner from "../shared/spinner";
import { cn } from "../utls/utils";

function ChatMessagesLayout() {
  const { user } = useAuth();
  const {
    messages,
    loadMoreMessages,

    hasMoreMessages,
    isLoading,
  } = useMessageContext();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const isInitialLoad = useRef(true);
  const [visibleCount, setVisibleCount] = useState(10);
  const [responseId, setResponseId] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const allMessages = messages || [];
  const sortedMessages = [...allMessages].sort((a, b) => {
    const aTime = new Date(a.created_at || 0).getTime();
    const bTime = new Date(b.created_at || 0).getTime();

    if (aTime !== bTime) {
      return aTime - bTime;
    }

    return String(a.id || "").localeCompare(String(b.id || ""));
  });

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const { streamingMessageId, streamedText, marqueeMessages } =
    useEventStreaming();
  const toolCallText = marqueeMessages.length ? marqueeMessages.join("\n") : "";

  useEffect(() => {
    if (isInitialLoad.current) {
      setVisibleCount(Math.min(10, sortedMessages.length));
      isInitialLoad.current = false;
    } else if (sortedMessages.length > visibleCount) {
      setVisibleCount(sortedMessages.length);
    }
  }, [sortedMessages.length, visibleCount]);

  useEffect(() => {
    if (streamingMessageId && (streamedText || toolCallText)) {
      scrollToBottom();
    }
  }, [streamedText, toolCallText, streamingMessageId]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setVisibleCount((prev) => {
            const next = Math.min(prev + 10, sortedMessages.length);
            if (next >= sortedMessages.length && hasMoreMessages) {
              loadMoreMessages();
            }
            return next;
          });
        }
      },
      {
        root: containerRef.current,
        threshold: 1,
      }
    );

    const node = loadMoreRef.current;
    if (node) {
      observer.observe(node);
    }
    return () => {
      if (node) {
        observer.unobserve(node);
      }
    };
  }, [sortedMessages.length, loadMoreMessages, hasMoreMessages]);

  if (!sortedMessages || sortedMessages.length === 0) {
    return (
      <div className="h-full flex items-center justify-center bg-white rounded-lg shadow-sm">
        <p className="text-brown-400">No messages yet. Start a conversation!</p>
      </div>
    );
  }

  const startIndex = Math.max(sortedMessages.length - visibleCount, 0);
  const visibleMessages = sortedMessages.slice(startIndex);

  return (
    <div
      className="h-full overflow-y-auto bg-white rounded-lg shadow-sm max-w-4xl mx-auto"
      ref={containerRef}
    >
      <div className="flex flex-col space-y-4 p-4">
        <div ref={loadMoreRef} />
        {visibleMessages.map((message: MessageRow, index: number) => {
          const key = `${message.id || (message as any)._tempId}-${index}`;

          if (message.role === MessageRole.ASSISTANT) {
            return (
              <div key={key}>
                <AssistantChatItem message={message} />
              </div>
            );
          }

          const isUser =
            message.role === MessageRole.USER && message.user_id === user?.id;

          return (
            <div
              key={key}
              className={cn(
                "flex flex-row items-center gap-2",
                isLoading && "opacity-70",
                isUser ? "justify-end" : "justify-start"
              )}
            >
              <UserChatItem
                message={message}
                align={isUser ? "end" : "start"}
              />
              {!streamingMessageId && (
                <SubmitButton
                  size="sm"
                  color="beige"
                  onClick={() => {
                    setResponseId(message.response_id || "");
                    setIsModalOpen(true);
                  }}
                  icon={<EyeIcon className="w-4 h-4" />}
                  iconOnly={true}
                />
              )}
              {streamingMessageId && streamingMessageId === message.id && (
                <div className="text-xs text-terracotta-500 text-right mr-2 mt-1">
                  Sending...
                </div>
              )}
            </div>
          );
        })}

        <div key="streaming-message">
          {streamingMessageId && !streamedText && <ThemedSpinner />}
          {streamedText && (
            <AssistantChatItem
              message={
                {
                  id: streamingMessageId || "",
                  content: streamedText,
                  role: MessageRole.ASSISTANT,
                  created_at: new Date().toISOString(),
                  updated_at: new Date().toISOString(),
                  generated_by: "web_app",
                  message_file_store: [],
                  user_id: null,
                  user: {},
                  agent_id: null,
                  deleted_at: null,
                  group_chat_id: null,
                  is_deleted: null,
                  message_type: null,
                  project_id: null,
                  reply_to_message_id: null,
                  response_id: null,
                  run_id: null,
                  thread_id: null,
                  message_feedback: undefined,
                } as MessageRow
              }
            />
          )}
        </div>

        {toolCallText && (
          <div key="tool-call">
            <ToolCallItem text={toolCallText} />
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>
      <RunStepsModal
        isOpen={isModalOpen}
        responseId={responseId}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
}

export default ChatMessagesLayout;
